import AxiosInstance from '@/api/axios';
import { ApiDataItem } from '@/pages/Rule';
import { useAppSelector } from '@/stores/hooks';
import { RootState } from '@/stores/store';
import { checkKeepme, checkUserKeepme } from '@/utils/checkAccess';
import { Channel, Platform } from '@/utils/enum';
import { getClientId, isLegacyWhatsappTemplate } from '@/utils/helper';
import { Template } from '@/utils/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Info } from 'lucide-react';
import {
  Dispatch,
  SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';
import Button from '../Base/Button';
import CheckboxSelectV2 from '../Base/CheckboxSelect/CheckoboxSelectV2';
import { FormCheck, FormInput, FormLabel, FormSelect } from '../Base/Form';
import { Dialog } from '../Base/Headless';
import Lucide from '../Base/Lucide';
import Table from '../Base/Table';
import Tippy from '../Base/Tippy';
import { toast } from '../Base/Toast/ToastManager';
import Loading from '../Loading/Loading';
import Pill from '../Pill';
import RenderMultiSelect from '../RenderMultiSelect';
import RuleEvent from '../RuleEvent';
import TemplatePreview, { IPreviewTemplate } from '../TemplatePreview';
import { AgentSettings } from '../TemplatesPanel';
import { LEGACY_WHATSAPP_TEMPLATE_MESSAGE } from '../TimezoneRegion/constants';
import ViewTwilioTemplate from '../ViewTwilioTemplate';

export const recurrenceEndsSchema = z.object({
  type: z.enum(['never', 'on_date', 'after_occurrences']).default('never'),
  date: z.string().optional(),
  occurrences: z.coerce.number().optional(),
});

export const recurrenceSchema = z.object({
  interval: z.coerce.number().min(1).default(1),
  frequency: z.enum(['day', 'week', 'month', 'year']).default('day'),
  byWeekday: z.array(z.string()).optional(),
  monthlyMode: z.enum(['day_of_month', 'day_of_week']).optional(),
  monthlyDay: z.coerce.number().optional(),
  monthlyWeekdayOrdinal: z.coerce.number().optional(),
  monthlyWeekday: z.string().optional(),
  ends: recurrenceEndsSchema,
});

export const campaignDaySchema = z.object({
  channel: z.array(z.string()).min(1, 'Method is required'),
  timing: z.string().min(1, 'Time is required'),
  day: z.string().optional(),
  recurrence: recurrenceSchema.optional(),
  template: z.string().optional(),
  source_group: z.array(z.string()).optional(),
  source_name: z.array(z.string()).optional(),
  secondary: z
    .object({
      _id: z.string().optional(),
      channel: z.array(z.string()).min(1, 'Method is required'),
      template: z.string().optional(),
    })
    .optional(),
});

export type CampaignDayInputType = z.infer<typeof campaignDaySchema>;

const WEEKDAYS = [
  { value: 'SU', label: 'S', full: 'Sunday' },
  { value: 'MO', label: 'M', full: 'Monday' },
  { value: 'TU', label: 'T', full: 'Tuesday' },
  { value: 'WE', label: 'W', full: 'Wednesday' },
  { value: 'TH', label: 'T', full: 'Thursday' },
  { value: 'FR', label: 'F', full: 'Friday' },
  { value: 'SA', label: 'S', full: 'Saturday' },
] as const;

const WEEKDAY_ORDINALS = [
  { value: '1', label: 'First' },
  { value: '2', label: 'Second' },
  { value: '3', label: 'Third' },
  { value: '4', label: 'Fourth' },
  { value: '-1', label: 'Last' },
] as const;

const DEFAULT_RECURRENCE: z.infer<typeof recurrenceSchema> = {
  interval: 1,
  frequency: 'day',
  byWeekday: [],
  monthlyMode: 'day_of_month',
  monthlyDay: 1,
  monthlyWeekdayOrdinal: 1,
  monthlyWeekday: 'MO',
  ends: { type: 'never' },
};

const formatRecurrence = (
  recurrence: z.infer<typeof recurrenceSchema> | undefined
) => {
  if (!recurrence) return '—';
  const interval = Number(recurrence.interval) || 1;
  const frequency = recurrence.frequency || 'day';
  let summary = `Every ${interval} ${interval === 1 ? frequency : `${frequency}s`}`;

  if (frequency === 'week' && recurrence.byWeekday?.length) {
    summary += ` on ${recurrence.byWeekday
      .map((d) => WEEKDAYS.find((w) => w.value === d)?.full.slice(0, 3) ?? d)
      .join(', ')}`;
  }

  if (frequency === 'month') {
    if (recurrence.monthlyMode === 'day_of_week' && recurrence.monthlyWeekday) {
      const ordinal =
        WEEKDAY_ORDINALS.find(
          (o) => o.value === String(recurrence.monthlyWeekdayOrdinal ?? '1')
        )?.label ?? 'First';
      const weekday =
        WEEKDAYS.find((w) => w.value === recurrence.monthlyWeekday)?.full ??
        recurrence.monthlyWeekday;
      summary += ` on the ${ordinal.toLowerCase()} ${weekday}`;
    } else if (recurrence.monthlyDay) {
      summary += ` on day ${recurrence.monthlyDay}`;
    }
  }

  if (recurrence.ends?.type === 'on_date' && recurrence.ends.date) {
    summary += `, until ${recurrence.ends.date}`;
  } else if (
    recurrence.ends?.type === 'after_occurrences' &&
    recurrence.ends.occurrences
  ) {
    summary += `, ${recurrence.ends.occurrences} time(s)`;
  }

  return summary;
};

interface IAddCampaignProps {
  totalTemplates: Template[];
  agent: AgentSettings | null;
  campaignRules: CampaignDayInputType[];
  setCampaignRules: Dispatch<SetStateAction<CampaignDayInputType[]>>;
  setCampaignName: Dispatch<SetStateAction<string>>;
  campaignName: string;
  ruleEditOnly: boolean;
  campaignType: 'standard' | 'recurring';
  setCampaignType: Dispatch<SetStateAction<'standard' | 'recurring'>>;
  automationType?: string;
}

const UNIQUE_CHANNELS = ['Email', 'SMS', 'WhatsApp', 'Phone', 'Viber'] as const;

const CampaignRules = ({
  totalTemplates,
  agent,
  campaignRules,
  setCampaignRules,
  campaignName,
  setCampaignName,
  ruleEditOnly,
  campaignType,
  setCampaignType,
  automationType,
}: IAddCampaignProps) => {
  const client = getClientId();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<CampaignDayInputType>({
    resolver: zodResolver(campaignDaySchema),
  });

  const selectedChannels = watch('channel');
  const secondaryChannels = watch('secondary.channel');

  const [filterTemplates, setFilterTemplates] = useState<Template[]>([]);
  const [filterSecondaryTemplates, setFilterSecondaryTemplates] = useState<
    Template[]
  >([]);
  const [hasSecondary, setHasSecondary] = useState(false);
  const [dialogKey, setDialogKey] = useState(0);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);

  const templateRef = useRef<Template | null>(null);
  const [template, setTemplate] = useState<IPreviewTemplate | null>(null);
  const [bookingIndex, setBookingIndex] = useState(0);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const templates = useAppSelector((state: RootState) => state.templates.data);
  const readOnlyUser = useMemo(() => checkUserKeepme() || checkKeepme(), []);

  const getFilteredTemplates = useCallback(
    (channels: string[] | undefined): Template[] => {
      if (!channels?.length) return [];
      const channelLower = channels[0]?.toLowerCase();
      const platform =
        campaignType === 'recurring' && automationType
          ? automationType
          : Platform.reEngage;
      let source: Template[] = [];
      if (channelLower === 'mail') source = templates?.emailTemplate ?? [];
      else if (channelLower === 'sms') source = templates?.smsTemplate ?? [];
      else if (channelLower === 'whatsapp')
        source = templates?.whatsappTemplate ?? [];
      else if (channelLower === 'viber')
        source = templates?.viberTemplate ?? [];
      return source.filter(
        (t) => t.platform === platform || t.platform === Platform.reEngage
      );
    },
    [templates, campaignType, automationType]
  );

  const openAdd = () => {
    reset({});
    setValue('channel', []);
    setValue('secondary', undefined as any);
    setValue('template', '');
    setValue('day', '');
    setValue('timing', '');
    setValue(
      'recurrence',
      campaignType === 'recurring' ? DEFAULT_RECURRENCE : (undefined as any)
    );
    setHasSecondary(false);
    setSelectedGroups([]);
    setSelectedNames([]);
    setFilterTemplates([]);
    setFilterSecondaryTemplates([]);
    setBookingIndex(campaignRules.length);
    setDialogKey((k) => k + 1);
    setShowAddDialog(true);
  };

  const openEdit = (index: number) => {
    const rule = campaignRules[index];
    reset({
      channel: rule.channel,
      template: rule.template,
      timing: rule.timing,
      day: rule.day,
      recurrence:
        campaignType === 'recurring'
          ? (rule.recurrence ?? DEFAULT_RECURRENCE)
          : undefined,
      secondary: rule.secondary
        ? {
            channel: rule.secondary.channel,
            template: rule.secondary.template,
          }
        : undefined,
    });
    setHasSecondary(!!rule.secondary);
    setSelectedGroups(rule.source_group || []);
    setSelectedNames(rule.source_name || []);
    setFilterTemplates(getFilteredTemplates(rule.channel));
    setFilterSecondaryTemplates(
      rule.secondary ? getFilteredTemplates(rule.secondary.channel) : []
    );
    setBookingIndex(index);
    setDialogKey((k) => k + 1);
    setShowAddDialog(true);
  };

  const handleSetPreview = (index: number) => {
    const isWhatsapp =
      campaignRules[index].channel?.[0]?.toLowerCase() === 'whatsapp';
    const found = totalTemplates.find(
      (t) => t._id?.toString() === campaignRules[index]?.template?.toString()
    );
    if (!found && !isWhatsapp) {
      toast.show({
        title: '',
        content: 'Please select a template.',
        duration: 2000,
        type: 'warning',
      });
      return;
    }
    templateRef.current = found ?? null;
    setTemplate(found ?? null);
  };

  const [leadMap, setLeadMap] = useState<ApiDataItem[]>([]);

  useEffect(() => {
    const fetchMaps = async () => {
      try {
        const response = await AxiosInstance.get(`/leads/map/${client}`);
        if (response.data.data && Array.isArray(response.data.data)) {
          setLeadMap(response.data.data);
        } else {
          console.error('Unexpected response format:', response.data);
        }
      } catch (error) {
        console.error('Error fetching map data:', error);
      }
    };
    fetchMaps();
  }, [client]);

  const groups = Array.from(new Set(leadMap.map((item) => item.source_group)));
  const names = Array.from(new Set(leadMap.map((item) => item.source_name)));

  const onSubmit = (data: CampaignDayInputType) => {
    if (campaignType === 'standard' && !data.day) {
      toast.show({
        title: '',
        content: 'Day is required for standard campaigns.',
        duration: 2000,
        type: 'warning',
      });
      return;
    }
    if (
      campaignType === 'recurring' &&
      data.recurrence?.frequency === 'week' &&
      !data.recurrence.byWeekday?.length
    ) {
      toast.show({
        title: '',
        content: 'Please select at least one day of the week.',
        duration: 2000,
        type: 'warning',
      });
      return;
    }
    const payload: CampaignDayInputType = {
      ...data,
      source_group: selectedGroups,
      source_name: selectedNames,
      secondary: hasSecondary ? data.secondary : undefined,
    };

    setCampaignRules((prev) => {
      const isEdit = bookingIndex >= 0 && bookingIndex < prev.length;
      if (isEdit) {
        const updated = [...prev];
        updated[bookingIndex] = payload;
        return updated;
      }
      return [...prev, payload];
    });

    reset();
    setHasSecondary(false);
    setShowAddDialog(false);
  };

  const deleteCampaignRule = () => {
    setCampaignRules((prev) =>
      bookingIndex >= 0 && bookingIndex < prev.length
        ? prev.filter((_, i) => i !== bookingIndex)
        : prev
    );
    setShowDeleteDialog(false);
  };

  const currentRule = campaignRules[bookingIndex];

  const handleChannelChange = useCallback(
    (channels: string[], isSecondary: boolean) => {
      if (isSecondary) {
        setFilterSecondaryTemplates(getFilteredTemplates(channels));
      } else {
        setFilterTemplates(getFilteredTemplates(channels));
      }
    },
    [getFilteredTemplates]
  );

  const renderRecurrenceFields = () => {
    const interval = watch('recurrence.interval') || 1;
    const frequency = watch('recurrence.frequency') || 'day';
    const byWeekday = watch('recurrence.byWeekday') ?? [];
    const monthlyMode = watch('recurrence.monthlyMode') ?? 'day_of_month';
    const endsType = watch('recurrence.ends.type') ?? 'never';

    const toggleWeekday = (day: string) => {
      const next = byWeekday.includes(day)
        ? byWeekday.filter((d) => d !== day)
        : [...byWeekday, day];
      setValue('recurrence.byWeekday' as any, next);
    };

    return (
      <div className='flex flex-col gap-4'>
        {/* Repeat every */}
        <div className='grid grid-cols-3 gap-4 items-center'>
          <FormLabel className='col-span-1 text-left'>Repeat every</FormLabel>
          <div className='col-span-2 flex items-center gap-2'>
            <FormInput
              {...register('recurrence.interval' as any)}
              type='number'
              min={1}
              className='w-20'
            />
            <FormSelect
              {...register('recurrence.frequency' as any)}
              className='w-32'
            >
              <option value='day'>
                {Number(interval) === 1 ? 'day' : 'days'}
              </option>
              <option value='week'>
                {Number(interval) === 1 ? 'week' : 'weeks'}
              </option>
              <option value='month'>
                {Number(interval) === 1 ? 'month' : 'months'}
              </option>
              <option value='year'>
                {Number(interval) === 1 ? 'year' : 'years'}
              </option>
            </FormSelect>
          </div>
        </div>

        {/* Weekly: day-of-week picker */}
        {frequency === 'week' && (
          <div className='grid grid-cols-3 gap-4 items-center'>
            <FormLabel className='col-span-1 text-left'>Repeat on</FormLabel>
            <div className='col-span-2 flex items-center gap-1'>
              {WEEKDAYS.map((d) => {
                const active = byWeekday.includes(d.value);
                return (
                  <button
                    key={d.value}
                    type='button'
                    onClick={() => toggleWeekday(d.value)}
                    title={d.full}
                    className={`w-8 h-8 rounded-full text-xs font-medium border transition-colors ${
                      active
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-slate-600 border-slate-300/60'
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Monthly: on day X vs on the Nth weekday */}
        {frequency === 'month' && (
          <div className='grid grid-cols-3 gap-4 items-start'>
            <FormLabel className='col-span-1 text-left pt-2'>
              Repeat on
            </FormLabel>
            <div className='col-span-2 flex flex-col gap-2'>
              <FormCheck>
                <FormCheck.Input
                  type='radio'
                  id='monthly-day-of-month'
                  checked={monthlyMode === 'day_of_month'}
                  onChange={() =>
                    setValue('recurrence.monthlyMode' as any, 'day_of_month')
                  }
                />
                <FormCheck.Label
                  htmlFor='monthly-day-of-month'
                  className='flex items-center gap-2'
                >
                  Monthly on day
                  <FormInput
                    {...register('recurrence.monthlyDay' as any)}
                    type='number'
                    min={1}
                    max={31}
                    className='w-16'
                    disabled={monthlyMode !== 'day_of_month'}
                    onFocus={() =>
                      setValue('recurrence.monthlyMode' as any, 'day_of_month')
                    }
                  />
                </FormCheck.Label>
              </FormCheck>
              <FormCheck>
                <FormCheck.Input
                  type='radio'
                  id='monthly-day-of-week'
                  checked={monthlyMode === 'day_of_week'}
                  onChange={() =>
                    setValue('recurrence.monthlyMode' as any, 'day_of_week')
                  }
                />
                <FormCheck.Label
                  htmlFor='monthly-day-of-week'
                  className='flex items-center gap-2'
                >
                  Monthly on the
                  <FormSelect
                    {...register('recurrence.monthlyWeekdayOrdinal' as any)}
                    className='w-24'
                    disabled={monthlyMode !== 'day_of_week'}
                    onFocus={() =>
                      setValue('recurrence.monthlyMode' as any, 'day_of_week')
                    }
                  >
                    {WEEKDAY_ORDINALS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </FormSelect>
                  <FormSelect
                    {...register('recurrence.monthlyWeekday' as any)}
                    className='w-28'
                    disabled={monthlyMode !== 'day_of_week'}
                    onFocus={() =>
                      setValue('recurrence.monthlyMode' as any, 'day_of_week')
                    }
                  >
                    {WEEKDAYS.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.full}
                      </option>
                    ))}
                  </FormSelect>
                </FormCheck.Label>
              </FormCheck>
            </div>
          </div>
        )}

        {/* Ends */}
        <div className='grid grid-cols-3 gap-4 items-start'>
          <FormLabel className='col-span-1 text-left pt-2'>Ends</FormLabel>
          <div className='col-span-2 flex flex-col gap-2'>
            <FormCheck>
              <FormCheck.Input
                type='radio'
                id='ends-never'
                checked={endsType === 'never'}
                onChange={() =>
                  setValue('recurrence.ends.type' as any, 'never')
                }
              />
              <FormCheck.Label htmlFor='ends-never'>Never</FormCheck.Label>
            </FormCheck>
            <FormCheck>
              <FormCheck.Input
                type='radio'
                id='ends-on-date'
                checked={endsType === 'on_date'}
                onChange={() =>
                  setValue('recurrence.ends.type' as any, 'on_date')
                }
              />
              <FormCheck.Label
                htmlFor='ends-on-date'
                className='flex items-center gap-2'
              >
                On
                <FormInput
                  {...register('recurrence.ends.date' as any)}
                  type='date'
                  className='w-40'
                  disabled={endsType !== 'on_date'}
                  onFocus={() =>
                    setValue('recurrence.ends.type' as any, 'on_date')
                  }
                />
              </FormCheck.Label>
            </FormCheck>
            <FormCheck>
              <FormCheck.Input
                type='radio'
                id='ends-after'
                checked={endsType === 'after_occurrences'}
                onChange={() =>
                  setValue('recurrence.ends.type' as any, 'after_occurrences')
                }
              />
              <FormCheck.Label
                htmlFor='ends-after'
                className='flex items-center gap-2'
              >
                After
                <FormInput
                  {...register('recurrence.ends.occurrences' as any)}
                  type='number'
                  min={1}
                  className='w-20'
                  disabled={endsType !== 'after_occurrences'}
                  onFocus={() =>
                    setValue('recurrence.ends.type' as any, 'after_occurrences')
                  }
                />
                occurrence(s)
              </FormCheck.Label>
            </FormCheck>
          </div>
        </div>
      </div>
    );
  };

  const renderRuleFields = (
    prefix: '' | 'secondary.',
    filteredTmpl: Template[],
    watchedChannels: string[] | undefined,
    sectionErrors: any,
    disallowedChannels?: string[]
  ) => {
    const isSecondary = prefix === 'secondary.';
    return (
      <div className='flex flex-col gap-4'>
        {/* Method */}
        <div className='grid grid-cols-3 gap-4 items-center'>
          <FormLabel className='col-span-1 text-left'>Method</FormLabel>
          <div className='col-span-2'>
            <CheckboxSelectV2
              selected={
                isSecondary
                  ? (secondaryChannels ?? [])
                  : (selectedChannels ?? [])
              }
              options={Channel}
              setValue={(name, value) => {
                setValue(name as any, value);
                handleChannelChange(value as string[], isSecondary);
              }}
              name={isSecondary ? 'secondary.channel' : 'channel'}
              count={1}
              disabledOptions={disallowedChannels}
            />
          </div>
          {sectionErrors?.channel && (
            <p className='text-red-500 text-xs italic col-span-2 text-left'>
              {sectionErrors.channel.message}
            </p>
          )}
        </div>

        {/* Time */}
        {!isSecondary && (
          <div className='grid grid-cols-3 gap-4 items-center'>
            <FormLabel className='col-span-1 text-left'>Time</FormLabel>
            <FormInput
              {...register(`${prefix}timing` as any)}
              type='time'
              className='col-span-2'
            />
            {sectionErrors?.timing && (
              <p className='text-red-500 text-xs italic col-span-2 text-left'>
                {sectionErrors.timing.message}
              </p>
            )}
          </div>
        )}

        {!isSecondary && campaignType === 'standard' && (
          <div className='grid grid-cols-3 gap-4 items-center'>
            <FormLabel className='col-span-1 text-left'>Day</FormLabel>
            <div className='col-span-2 flex items-center gap-2'>
              <FormInput
                {...register(`${prefix}day` as any)}
                type='number'
                placeholder='e.g., 0 = Same Day, 1 = Tomorrow'
                className='w-full'
              />
            </div>
            {sectionErrors?.day && (
              <p className='text-red-500 text-xs italic col-span-2 text-left'>
                {sectionErrors.day.message}
              </p>
            )}
          </div>
        )}

        {!isSecondary &&
          campaignType === 'recurring' &&
          renderRecurrenceFields()}

        {/* Template */}
        {Array.isArray(watchedChannels) &&
          !watchedChannels.includes('Phone') && (
            <div className='grid grid-cols-3 items-center gap-4'>
              <FormLabel className='col-span-1 text-left'>Template</FormLabel>
              <div className='col-span-2 flex gap-2 items-center'>
                <select
                  {...register(`${prefix}template` as any)}
                  value={
                    isSecondary
                      ? (watch('secondary.template') ?? '')
                      : (watch('template') ?? '')
                  }
                  className='w-full bg-white border border-slate-300/60 rounded-md p-2 cursor-pointer text-sm h-9'
                  onChange={(e) =>
                    setValue(`${prefix}template` as any, e.target.value)
                  }
                >
                  <option value=''>Select Template</option>
                  {filteredTmpl.map((t, index) => (
                    <option key={index} value={t._id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                <Tippy content='Templates can be customized in the Templates section within Account > Templates.'>
                  <Info />
                </Tippy>
              </div>
              {sectionErrors?.template && (
                <p className='text-red-500 text-xs italic text-left'>
                  {sectionErrors.template.message}
                </p>
              )}
            </div>
          )}
      </div>
    );
  };

  return (
    <>
      {!ruleEditOnly && (
        <div className='flex flex-col gap-3 p-5'>
          <div className='flex items-center gap-5'>
            <FormLabel htmlFor='campaign'>Name</FormLabel>
            <FormInput
              id='campaign'
              placeholder='Campaign Name'
              onChange={(e) => setCampaignName(e.target.value)}
              value={campaignName}
            />
          </div>
          <div className='flex items-center gap-5'>
            <FormLabel>Type</FormLabel>
            <span className='inline-flex items-center gap-1 text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full capitalize'>
              {campaignType}
            </span>
          </div>
        </div>
      )}

      <div className='box p-5 mb-8'>
        <div className='w-full flex items-center justify-between'>
          <div>
            <Pill subTitle={campaignName} variant='DEFAULT' />
            <p className='mb-2'>
              Choose your preferred contact method and set the time delay for
              campaign.
            </p>
          </div>
        </div>

        <div className='w-full mb-4 h-auto overflow-x-auto'>
          {campaignRules.length > 0 && (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  {[
                    'Method',
                    ...(campaignType === 'standard' ? ['Day'] : ['Every']),
                    'At Time',
                    'Template',
                    'Source Group',
                    'Source Name',
                    'Secondary Template',
                    ...(readOnlyUser ? [] : ['Action']),
                  ].map((col) => (
                    <Table.Td
                      key={col}
                      className={`py-4 font-medium border-t bg-slate-50 border-slate-200/60 text-slate-500 ${col === 'Method' ? 'w-[10rem]' : ''} ${col === 'Action' ? 'text-right pr-16' : ''}`}
                    >
                      {col}
                    </Table.Td>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {campaignRules.map((rule, index) => {
                  const selectedTemplate = totalTemplates.find(
                    (t) => t._id?.toString() === rule.template?.toString()
                  );
                  const secondaryTemplate = rule.secondary
                    ? totalTemplates.find(
                        (t) =>
                          t._id?.toString() ===
                          rule.secondary?.template?.toString()
                      )
                    : null;
                  const timing = new Date(
                    `1970-01-01T${rule.timing}`
                  ).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                  });
                  const legacy = isLegacyWhatsappTemplate(
                    rule.channel,
                    selectedTemplate
                  );
                  return (
                    <Table.Tr key={index} className='[&_td]:last:border-b-0'>
                      <Table.Td className='w-[10rem] py-2 border-dashed dark:bg-darkmode-600'>
                        {rule.channel.map((ch, i) => (
                          <span className='ml-2 inline-block' key={i}>
                            {ch}
                          </span>
                        ))}
                      </Table.Td>
                      <Table.Td className='py-2 border-dashed dark:bg-darkmode-600 truncate'>
                        {campaignType === 'standard'
                          ? rule.day
                            ? `Day ${rule.day}`
                            : '—'
                          : formatRecurrence(rule.recurrence)}
                      </Table.Td>
                      <Table.Td className='py-2 border-dashed dark:bg-darkmode-600 truncate'>
                        {timing}
                      </Table.Td>
                      <Table.Td
                        className={`${legacy ? 'text-warning' : ''} py-2 border-dashed dark:bg-darkmode-600 truncate`}
                      >
                        {legacy ? (
                          <Tippy content={LEGACY_WHATSAPP_TEMPLATE_MESSAGE}>
                            <Lucide
                              icon='Info'
                              className='w-4 inline-block mr-2 mb-1'
                            />
                            {selectedTemplate?.name ?? 'No Template'}
                          </Tippy>
                        ) : (
                          (selectedTemplate?.name ?? 'No Template')
                        )}
                      </Table.Td>
                      <Table.Td className='py-2 border-dashed dark:bg-darkmode-600 truncate min-w-[12rem]'>
                        {Array.isArray(rule.source_group) &&
                        rule.source_group.length > 0 ? (
                          <FormSelect>
                            {rule.source_group.map((option, index) => (
                              <option key={index}>{option}</option>
                            ))}
                          </FormSelect>
                        ) : (
                          'All'
                        )}
                      </Table.Td>
                      <Table.Td className='py-2 border-dashed dark:bg-darkmode-600 truncate min-w-[12rem]'>
                        {Array.isArray(rule.source_name) &&
                        rule.source_name.length > 0 ? (
                          <FormSelect>
                            {rule.source_name.map((option, index) => (
                              <option key={index}>{option}</option>
                            ))}
                          </FormSelect>
                        ) : (
                          'All'
                        )}
                      </Table.Td>
                      <Table.Td className='py-2 border-dashed dark:bg-darkmode-600 truncate'>
                        {secondaryTemplate ? (
                          <span className='inline-flex items-center gap-1'>
                            {secondaryTemplate.name}
                          </span>
                        ) : (
                          <span className='text-slate-400 text-xs'>—</span>
                        )}
                      </Table.Td>
                      {!readOnlyUser && (
                        <Table.Td className='py-2 border-dashed dark:bg-darkmode-600 text-right flex items-center gap-2 justify-end'>
                          {rule.channel.find((ch) => ch !== 'Phone') &&
                            (selectedTemplate?.sid ? (
                              <ViewTwilioTemplate sid={selectedTemplate.sid} />
                            ) : (
                              <TemplatePreview
                                platform={Platform.reEngage}
                                variant='plain'
                                handleShowPreview={() =>
                                  handleSetPreview(index)
                                }
                                template={{
                                  ...template,
                                  timezone: templates?.client?.timezone,
                                }}
                                method={rule.channel?.[0] || ''}
                                agent={{
                                  branch: agent?.branch ?? '',
                                  location_logo:
                                    agent?.headerMedia?.path ?? null,
                                  logo_height: agent?.emailImageHeight ?? '7vh',
                                }}
                              />
                            ))}
                          <Button
                            type='button'
                            variant='plain'
                            onClick={() => openEdit(index)}
                          >
                            <Lucide icon='Pencil' className='mr-2 w-4' />
                            Edit
                          </Button>
                          <Tippy content='Delete'>
                            <Button
                              type='button'
                              variant='plain'
                              onClick={() => {
                                setBookingIndex(index);
                                setShowDeleteDialog(true);
                              }}
                            >
                              <Lucide icon='Trash' />
                            </Button>
                          </Tippy>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}

          {campaignRules.length < UNIQUE_CHANNELS.length && !readOnlyUser && (
            <Button
              className='h-[2.25rem] mt-4 w-fit flex gap-2 px-4 text-xs'
              type='button'
              onClick={openAdd}
              variant='outline-primary'
            >
              <Lucide icon='Plus' />
              Add Rule
            </Button>
          )}
        </div>
      </div>

      {showAddDialog && (
        <Dialog
          size='xl'
          open={showAddDialog}
          onClose={() => setShowAddDialog(false)}
        >
          <Dialog.Panel className='p-3 text-center'>
            <Dialog.Title className='flex flex-col gap-3'>
              <Lucide
                icon='Clock'
                className='text-orange-600 w-[3rem] h-[3rem]'
              />
              <span className='text-xl md:text-3xl'>Campaign Rule</span>
              <span className='text-sm text-slate-700/80'>
                Choose your preferred contact method and set the time delay for
                trigger.
              </span>
            </Dialog.Title>
            <Dialog.Description>
              <form key={dialogKey} onSubmit={handleSubmit(onSubmit)}>
                <div className='w-full flex flex-col justify-start gap-4 mb-4'>
                  {renderRuleFields(
                    '',
                    filterTemplates,
                    selectedChannels,
                    errors,
                    secondaryChannels
                  )}

                  <div className='grid grid-cols-3 items-center gap-4'>
                    <FormLabel className='col-span-1 text-left'>
                      Select Source Group
                    </FormLabel>
                    {RenderMultiSelect(
                      'Group',
                      Array.from(new Set([...groups, ...selectedGroups])),
                      selectedGroups,
                      setSelectedGroups,
                      true
                    )}
                  </div>

                  <div className='grid grid-cols-3 items-center gap-4'>
                    <FormLabel className='col-span-1 text-left'>
                      Select Source Names
                    </FormLabel>
                    {RenderMultiSelect(
                      'Name',
                      Array.from(new Set([...names, ...selectedNames])),
                      selectedNames,
                      setSelectedNames,
                      true
                    )}
                  </div>
                </div>

                {/* Secondary Template Toggle */}
                {campaignType !== 'recurring' && (
                  <div className='border-t border-slate-200 pt-4 mt-2'>
                    <div className='flex items-center justify-between mb-3'>
                      <div className='flex items-center gap-2'>
                        <span className='text-sm font-medium text-slate-700'>
                          Secondary Template
                        </span>
                        <Tippy content='An optional fallback template sent as a follow-up on this same rule.'>
                          <Info className='w-4 h-4 text-slate-400' />
                        </Tippy>
                      </div>
                      <Button
                        type='button'
                        variant={hasSecondary ? 'danger' : 'outline-primary'}
                        className='flex items-center gap-1'
                        onClick={() => {
                          if (hasSecondary) {
                            setValue('secondary', undefined as any);
                            setFilterSecondaryTemplates([]);
                          }
                          setHasSecondary((v) => !v);
                        }}
                      >
                        <Lucide
                          icon={hasSecondary ? 'Minus' : 'Plus'}
                          className='w-3 h-3'
                        />
                        {hasSecondary ? 'Remove Secondary' : 'Add Secondary'}
                      </Button>
                    </div>

                    {hasSecondary && (
                      <div className='bg-slate-50 border border-slate-200 rounded-lg p-4 flex flex-col gap-4'>
                        {renderRuleFields(
                          'secondary.',
                          filterSecondaryTemplates,
                          secondaryChannels,
                          (errors as any).secondary,
                          // Disable channels already selected in primary to avoid duplicates
                          selectedChannels
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className='flex justify-center gap-x-3 mt-5'>
                  <Button
                    type='button'
                    variant='primary'
                    onClick={() => setShowAddDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button type='submit' variant='success'>
                    Save
                  </Button>
                </div>
              </form>
            </Dialog.Description>
          </Dialog.Panel>
        </Dialog>
      )}

      {showDeleteDialog && currentRule && (
        <Dialog
          size='xl'
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
        >
          <Dialog.Panel className='p-3 text-center'>
            <Dialog.Title className='flex flex-col gap-3'>
              <span className='text-xl md:text-3xl'>Delete campaign rule</span>
              <span className='text-sm text-slate-700/80'>
                This action cannot be undone.
              </span>
            </Dialog.Title>
            <Dialog.Description className='flex flex-col gap-4'>
              <div className='w-full'>
                <Suspense fallback={<Loading />}>
                  <RuleEvent
                    channel={currentRule.channel.toString()}
                    timing={currentRule.timing}
                    className='w-full'
                  />
                </Suspense>
              </div>
              <Dialog.Footer className='flex justify-center gap-2'>
                <Button onClick={() => setShowDeleteDialog(false)}>
                  Cancel
                </Button>
                <Button variant='danger' onClick={deleteCampaignRule}>
                  Delete campaign rule
                </Button>
              </Dialog.Footer>
            </Dialog.Description>
          </Dialog.Panel>
        </Dialog>
      )}
    </>
  );
};

export default CampaignRules;
